/**
 * Global help context — manages the drawer's open state, the current
 * page's help context, and the recently-viewed history. Mounts once at
 * the top of the app tree.
 *
 * Pages call `useHelpContext('vetting/inspection-prep')` to register
 * themselves; the floating Help button reads that to open to the right
 * article. The `?` keypress globally toggles the drawer (unless focus
 * is in an input / textarea / contenteditable).
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

export interface HelpState {
  open: boolean;
  /** The article slug currently being shown (null = topic landing). */
  slug: string | null;
  /** The page-level context key (e.g. 'vetting/inspection-prep'). */
  contextKey: string | null;
  /** Slugs the user has visited recently, most-recent first. */
  recent: string[];
}

interface HelpContextValue extends HelpState {
  /** Open the drawer; optionally to a specific slug. Falls back to context. */
  openHelp: (slug?: string | null) => void;
  closeHelp: () => void;
  toggleHelp: () => void;
  /** Pages register their help context for the FAB. */
  setContextKey: (key: string | null) => void;
  /** Mark a slug as viewed (adds to recent + bumps to front). */
  markViewed: (slug: string) => void;
}

const HelpContext = createContext<HelpContextValue | null>(null);

const RECENT_KEY = 'varuna-help-recent';
const RECENT_MAX = 5;

function loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string').slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

function saveRecent(slugs: string[]) {
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(slugs.slice(0, RECENT_MAX))); }
  catch { /* private mode etc. — non-fatal */ }
}

export function HelpProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [slug, setSlug] = useState<string | null>(null);
  const [contextKey, setContextKey] = useState<string | null>(null);
  const [recent, setRecent] = useState<string[]>(() => loadRecent());

  const openHelp = useCallback((s?: string | null) => {
    if (s !== undefined) setSlug(s);
    setOpen(true);
  }, []);

  const closeHelp = useCallback(() => setOpen(false), []);
  const toggleHelp = useCallback(() => setOpen((o) => !o), []);

  const markViewed = useCallback((s: string) => {
    setRecent((prev) => {
      const next = [s, ...prev.filter((x) => x !== s)].slice(0, RECENT_MAX);
      saveRecent(next);
      return next;
    });
  }, []);

  // Global ? to toggle — but not when typing.
  const openRef = useRef(open);
  openRef.current = open;
  useEffect(() => {
    function isInteractive(el: EventTarget | null): boolean {
      if (!(el instanceof HTMLElement)) return false;
      const tag = el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (el.isContentEditable) return true;
      return false;
    }
    function onKey(e: KeyboardEvent) {
      if (isInteractive(e.target)) return;
      // Plain '?' (Shift+/) toggles. Esc closes.
      if (e.key === '?' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        toggleHelp();
      } else if (e.key === 'Escape' && openRef.current) {
        e.preventDefault();
        closeHelp();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleHelp, closeHelp]);

  const value = useMemo<HelpContextValue>(() => ({
    open, slug, contextKey, recent,
    openHelp, closeHelp, toggleHelp,
    setContextKey, markViewed,
  }), [open, slug, contextKey, recent, openHelp, closeHelp, toggleHelp, markViewed]);

  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
}

export function useHelp(): HelpContextValue {
  const ctx = useContext(HelpContext);
  if (!ctx) throw new Error('useHelp must be used inside <HelpProvider>');
  return ctx;
}

/**
 * Pages call this once to declare their help context key. The floating
 * Help button uses this to choose a default article when opened. Cleared
 * on unmount so navigation doesn't leak stale context.
 */
export function useHelpContext(key: string) {
  const { setContextKey } = useHelp();
  useEffect(() => {
    setContextKey(key);
    return () => setContextKey(null);
  }, [key, setContextKey]);
}
